import { useCallback, useEffect, useState } from 'react';
import { AddClient, type RunAction } from './AddClient.tsx';
import { ImportClients } from './admin/ImportClients.tsx';
import {
  api,
  can,
  type ChaseTenant,
  type ExcessPermissionTenant,
  type MeView,
  type PlatformScores,
  type PortfolioTenant,
  type ReconsentTenant,
  type Tenant,
} from './api.ts';
import { AwaitingConsent } from './AwaitingConsent.tsx';
import {
  ArchivedClients,
  hasRowActions,
  type Capabilities,
  type DrawerKind,
  type Pending,
} from './ClientActions.tsx';
import { SkeletonRow, SortHeader, TenantRow } from './ClientRow.tsx';
import { SORTS, STALE_DAYS, toRow, type SortKey } from './clientRows.ts';
import { awaitingRows, clientViews, type ClientViewId } from './clientViews.ts';
import { message, plural } from './format.ts';
import { ExcessPermissionPanel, ReconsentPanel } from './OnboardingGrant.tsx';
import { ScoreDefinitionNote } from './platform/ScoreParts.tsx';
import { mayLoadScores, staffScoreFor } from './platform/scores.ts';
// admin.css first and portfolio.css second, deliberately. Vite emits CSS in
// module-graph order, and both files style this screen; where they write a rule
// at the same specificity — a margin on a block carrying both an `admin-` and a
// `cl-` class — the one named after this screen has to win.
import './admin/admin.css';
import './portfolio.css';
// The `ob-` rules, which the chase rows and both permission panels are built
// from. Imported by the screen rather than by the components, so that a panel
// cannot end up rendered without its layout by a file that forgot.
import './onboarding.css';

/**
 * Clients — the estate view, and the whole client lifecycle with it.
 *
 * Roadmap §6 Phase 2 calls this the product for an MSP buyer rather than a
 * convenience, and the brief for it is one sentence: sixty clients, Monday
 * morning, three seconds to decide who to deal with first. Everything on this
 * screen either serves that decision or is in its way.
 *
 * **Why there is one client list.** There were three. This screen ranked
 * clients by what needed attention; an Admin screen called "Client management"
 * listed the same clients and offered the verbs — add, edit, reconnect,
 * archive, purge; and an Onboarding screen listed the ones that had not
 * connected and offered the consent link. Which of the three an operator wanted
 * depended on what they intended to *do* next, and a menu label cannot say
 * that. So the lists merged: the verbs moved onto the rows, each gated on the
 * capability it needs, and the two onboarding surfaces became tabs — Awaiting
 * consent for the clients who need a link, Permissions for the connected ones
 * whose grant no longer matches what we ask for.
 *
 * There is no percentage anywhere here for a customer, on purpose. ADR-0007
 * refused a compliance score for the report and the same reasoning applies
 * harder to a screen that ranks customers against each other: a
 * severity-weighted number is indefensible when an auditor asks how it was
 * derived, and any denominator quietly buries the controls we could not
 * evidence — the ones most worth looking at. Counts and severity, never a score.
 *
 * The one exception is DriftWatch's own staff (ADR-0081), who see a score, its
 * coverage and a rank under each client's name, with the definition above the
 * table. The scores are requested only when `mayLoadScores(me)` is true and
 * reach a row only through `staffScoreFor(me, …)`, so for a customer there is
 * no request, no callout and not one changed element in the table. The order
 * stays by attention for staff too: a score is context, not the sort.
 *
 * **The lifecycle is two steps on purpose.** Archiving is a reversible soft
 * delete; the purge is refused by the server unless the tenant is already
 * archived, and this screen offers it only on an archived row and only behind a
 * typed-name confirmation. The copy states, out loud, that it is irreversible
 * and destroys the stored credential — a colour cannot carry that, and there is
 * no destructive button variant in this design (ADR: red is a verdict).
 *
 * The row model and the row itself live beside this file, in `clientRows.ts`
 * and `ClientRow.tsx`. This one is the screen: what is loaded, which view is
 * showing, and what a mutation does.
 *
 * The component is still exported as `Portfolio` because the shell imports it
 * by that name; the word the customer reads is "Clients".
 */

/**
 * The two ways clients arrive. Mutually exclusive because they are the same
 * operation at two scales, and a screen offering both at once invites an
 * operator to start typing into one while the other holds a consent link they
 * have not copied yet.
 */
type AddPanel = 'one' | 'many';

/**
 * The tab strip. `ClientViewId` is the three views the estate join produces;
 * Permissions is a fourth tab over a different payload entirely, so it is added
 * here rather than pushed into `clientViews.ts` — that module would have to
 * carry a count it cannot compute.
 */
type TabId = ClientViewId | 'permissions';

interface Props {
  readonly me: MeView;
  readonly onOpen: (tenant: Tenant) => void;
  /** Whether a client can be created at all on this deployment; see `AddClient`. */
  readonly entraConfigured: boolean;
}

export const Portfolio = ({ me, onOpen, entraConfigured }: Props) => {
  const [portfolio, setPortfolio] = useState<PortfolioTenant[]>([]);
  const [listed, setListed] = useState<Tenant[]>([]);
  const [chaseList, setChaseList] = useState<ChaseTenant[]>([]);
  const [reconsentList, setReconsentList] = useState<ReconsentTenant[]>([]);
  const [excessList, setExcessList] = useState<ExcessPermissionTenant[]>([]);
  const [needsChasing, setNeedsChasing] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [adding, setAdding] = useState<AddPanel | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [view, setView] = useState<TabId>('live');
  const [sort, setSort] = useState<SortKey>('risk');
  const [reversed, setReversed] = useState(false);
  const [scores, setScores] = useState<PlatformScores | null>(null);
  const [scoresError, setScoresError] = useState<string | null>(null);
  const staff = mayLoadScores(me);

  /*
   * Creating and correcting a client is `tenants.write`; anything that decides
   * whether a client exists at all — reconnect, archive, unarchive, purge — is
   * `deployment.manage`. Both are read from the capability list rather than
   * from the role, because what a role may do is the server's to decide.
   *
   * Gating add and import on `tenants.write` is a deliberate change from the
   * old Client management screen, which asked for `deployment.manage`. The
   * server asks `POST /tenants` for `tenants.write`, and the Onboarding screen
   * offered this same form behind that capability — so the old gate was both
   * stricter than the server and inconsistent with the other screen rendering
   * the identical panel: an engineer could add a client on one and not the
   * other. Both of those screens are this one now, and the looser gate is the
   * one that matches what the server will actually accept.
   *
   * This is a convenience either way. Every route behind every control here is
   * authorised again on the server, and hiding a button prevents nothing.
   */
  const capabilities: Capabilities = {
    canEdit: can(me, 'tenants.write'),
    canManage: can(me, 'deployment.manage'),
  };
  const { canEdit, canManage } = capabilities;

  /**
   * A zero-width space, appended and removed, so that repeating an action
   * repeats its announcement: a live region says nothing when the text it holds
   * has not changed, and archiving two clients in a row is two outcomes.
   */
  const announce = useCallback((text: string) => {
    setAnnouncement((previous) => (previous.endsWith('\u200B') ? text : `${text}\u200B`));
  }, []);

  /**
   * All three endpoints, every time. `GET /portfolio` filters archived clients
   * away in SQL and takes no parameter to stop it, and `GET /tenants` knows
   * nothing about any assessment — so the live rows come from the first, the
   * archived ones from the second, and `clientViews` joins them.
   *
   * `GET /tenants/onboarding` is the third because nothing else answers for the
   * consent link, the server's `daysWaiting`, the grant comparison behind the
   * Permissions tab, or `needsChasing`. It is loaded with the other two rather
   * than when its tab is opened: the two tab labels carry counts, and a count
   * that only becomes true once you have clicked the thing it describes is not
   * a count.
   */
  const load = useCallback(async () => {
    // Beside the three, not inside their `Promise.all`: scores are context for
    // staff, and a failure to compute them must not take the client list with it.
    if (staff) {
      void api
        .platformScores()
        .then((response) => {
          setScores(response);
          setScoresError(null);
        })
        .catch((cause: unknown) => setScoresError(message(cause)));
    }
    try {
      const [estate, tenants, onboarding] = await Promise.all([
        api.portfolio(),
        api.listTenants({ includeArchived: true }),
        api.onboarding(),
      ]);
      setPortfolio(estate.tenants);
      setListed(tenants.tenants);
      setChaseList(onboarding.chaseList);
      // Server order: oldest consent first, which is also the order this is
      // worked through. Nothing re-sorts either locally because nothing edits them.
      setReconsentList(onboarding.reconsentList);
      setExcessList(onboarding.excessPermissionList);
      setNeedsChasing(onboarding.summary.needsChasing);
      setError(null);
    } catch (cause) {
      setError(message(cause));
    } finally {
      setLoading(false);
    }
  }, [staff]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * One row, changed in place, for the actions on the Awaiting consent tab.
   *
   * A reload would be correct and would also redraw the table under the button
   * that was just pressed. What these actions change — `invitedAt`,
   * `reminderCount`, `lastRemindedAt`, `onboardingNote` — is on the portfolio
   * row, so that is where the patch lands; the chase payload is enrichment and
   * carries nothing any of them touch.
   */
  const patchRow = useCallback((id: string, patch: Partial<Tenant>) => {
    setPortfolio((previous) =>
      previous.map((tenant) => (tenant.id === id ? { ...tenant, ...patch } : tenant)),
    );
  }, []);

  /**
   * Reload behind the row that changed. `loading` is already false by the time
   * anybody can press anything, so nothing flashes: the table is replaced with
   * the same table plus whatever the server now says.
   */
  const refreshQuietly = useCallback(() => {
    void load();
  }, [load]);

  /**
   * Every mutation ends by reloading both lists rather than patching the row it
   * changed: an archive moves a row between tabs, and a purge removes it
   * entirely. Returns whether it worked, so a form can keep its fields on a
   * rejection rather than throwing away what the operator had typed.
   */
  const run: RunAction = async (action, said) => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await action();
      await load();
      setPending(null);
      setStatus(said);
      announce(said);
      return true;
    } catch (cause) {
      const text = message(cause);
      setError(text);
      announce(`That did not happen. ${text}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const views = clientViews(portfolio, listed);
  const grantWork = reconsentList.length + excessList.length;
  const tabs: ReadonlyArray<{ id: TabId; label: string; count: number }> = [
    { id: 'live', label: 'Live', count: views.counts.live },
    { id: 'awaiting', label: 'Awaiting consent', count: views.counts.awaiting },
    // Archived only when there is something in it. A permanently empty tab is
    // an invitation to check an answer the strip could have given.
    ...(views.counts.archived > 0
      ? [{ id: 'archived' as const, label: 'Archived', count: views.counts.archived }]
      : []),
    // Permissions on the same terms, and on an ordinary day it is not there at
    // all: every connected client is on the permission set this build asks for
    // and holds nothing it has stopped asking for. Its count is the two lists
    // together because they are two directions of one comparison.
    ...(grantWork > 0
      ? [{ id: 'permissions' as const, label: 'Permissions', count: grantWork }]
      : []),
  ];
  // Unarchiving the last archived client removes that tab from under whoever is
  // standing on it, so the chosen view is re-resolved against what still exists.
  const current = tabs.some((tab) => tab.id === view) ? view : 'live';

  const now = Date.now();
  const liveRows = views.live.map((tenant) => toRow(tenant, now));
  const spec = SORTS[sort];
  const ordered = [...liveRows].sort((a, b) =>
    reversed ? -spec.compare(a, b) : spec.compare(a, b),
  );
  const awaiting = awaitingRows(views.awaiting, chaseList, now);

  const failing = liveRows.filter((row) => row.fails > 0).length;
  const broken = liveRows.filter((row) => row.broken).length;
  const neverScanned = liveRows.filter(
    (row) => row.tenant.connectionState === 'connected' && row.scannedAt === null,
  ).length;
  const stale = liveRows.filter((row) => row.age !== null && row.age > STALE_DAYS).length;
  const unevidenced = liveRows.filter((row) => row.counts.INSUFFICIENT_DATA > 0).length;

  const selectedLive = views.live.filter((tenant) => selected.has(tenant.id));

  const toggleSelected = (id: string) => {
    setSelected((chosen) => {
      const next = new Set(chosen);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };

  const bulkArchive = async () => {
    const ids = selectedLive.map((tenant) => tenant.id);
    const names = selectedLive.map((tenant) => tenant.displayName);
    const ok = await run(async () => {
      for (const id of ids) await api.archiveTenant(id);
    }, `${plural(ids.length, 'client')} archived: ${names.join(', ')}.`);
    if (ok) setSelected(new Set());
  };

  const openRow = (tenantId: string, kind: DrawerKind | null) => {
    setAdding(null);
    setPending(kind === null ? null : { tenantId, kind });
  };

  const togglePanel = (panel: AddPanel) => {
    setAdding((open) => (open === panel ? null : panel));
    setPending(null);
  };

  const onSort = (key: SortKey) => {
    setReversed((backwards) => (key === sort ? !backwards : false));
    setSort(key);
  };

  const headerProps = { sort, reversed, onSort };
  const manageable = hasRowActions(capabilities);
  const columns = 5 + (canManage ? 1 : 0) + (manageable ? 1 : 0);
  const empty = !loading && views.counts.live === 0 && views.counts.archived === 0 && error === null;

  return (
    <>
      {/* No page title here: the shell's sticky header owns the breadcrumb and
          the name of this screen, and a second copy of "Clients" is just noise
          above the one thing the reader came for. */}
      <p className="lead cl-lead">
        Every client tenant this deployment holds, ordered by what needs attention first — and where
        a client is added, corrected, reconnected, archived and finally purged.
      </p>

      <p aria-live="polite" role="status" className="sr-only">
        {announcement}
      </p>

      {canManage && (
        <div className="callout admin-lifecycle cl-lifecycle">
          <span className="callout-title">Archiving is reversible. Purging is not.</span>
          Archiving takes a client out of every live list — the estate, the sweep, the consent chase —
          without touching a byte of their evidence, and it can be undone. Purging is offered only on
          a client that is already archived: it destroys every scan, finding and report they own, and
          the sealed credential DriftWatch reads them with. There is no recovering from it.
        </div>
      )}

      {error !== null && <div className="error cl-error">{error}</div>}
      {status !== null && <p className="status-inline cl-status">{status}</p>}

      {(canEdit || canManage) && (
        <div className="toolbar admin-actions">
          {canEdit && (
            <>
              <button
                type="button"
                className="btn btn-primary"
                aria-expanded={adding === 'one'}
                onClick={() => togglePanel('one')}
              >
                {adding === 'one' ? 'Cancel' : 'Add a client'}
              </button>
              <button
                type="button"
                className="btn btn-quiet"
                aria-expanded={adding === 'many'}
                onClick={() => togglePanel('many')}
              >
                {adding === 'many' ? 'Cancel' : 'Import a list'}
              </button>
            </>
          )}
          {canManage && selectedLive.length > 0 && (
            <>
              <span className="note admin-selected-note">
                {plural(selectedLive.length, 'client')} selected
              </span>
              <button
                type="button"
                className="btn btn-quiet"
                aria-busy={busy || undefined}
                disabled={busy}
                onClick={() => void bulkArchive()}
              >
                Archive selected
              </button>
            </>
          )}
        </div>
      )}

      {/* Wrapped only so the panel is spaced from the toolbar that opened it.
          Neither component carries a margin of its own — spacing is the job of
          whatever places it, which is what let both survive the screen they
          were first written for. */}
      {adding === 'one' && canEdit && (
        <div className="cl-panel">
          <AddClient
            entraConfigured={entraConfigured}
            announce={announce}
            onCreated={() => void load()}
            onCancel={() => setAdding(null)}
          />
        </div>
      )}

      {adding === 'many' && canEdit && (
        <div className="cl-panel">
          <ImportClients
            onImported={() => void load()}
            announce={announce}
            entraConfigured={entraConfigured}
          />
        </div>
      )}

      {loading && (
        <p className="note cl-loading" aria-live="polite">
          Reading each client’s connection and the counts from its latest assessment…
        </p>
      )}

      {empty ? (
        <EmptyEstate />
      ) : (
        <>
          {tabs.length > 1 && (
            <nav className="tabs-underline cl-tabs" aria-label="Client views">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className="tab"
                  aria-current={tab.id === current ? 'page' : undefined}
                  onClick={() => setView(tab.id)}
                >
                  {tab.label}
                  <span className="cl-tab-count num">{tab.count}</span>
                </button>
              ))}
            </nav>
          )}

          {current === 'archived' ? (
            <ArchivedClients
              tenants={views.archived}
              capabilities={capabilities}
              busy={busy}
              pending={pending}
              onOpen={openRow}
              onRun={run}
            />
          ) : current === 'permissions' ? (
            /* Both panels, unchanged from the screen they arrived on. Each
               renders only when it has rows; the tab itself only exists when
               one of them does, so this cannot be an empty page. */
            <div className="cl-permissions">
              {reconsentList.length > 0 && (
                <ReconsentPanel tenants={reconsentList} announce={announce} />
              )}
              {excessList.length > 0 && <ExcessPermissionPanel tenants={excessList} />}
            </div>
          ) : current === 'awaiting' ? (
            <AwaitingConsent
              rows={awaiting}
              needsChasing={needsChasing}
              entraConfigured={entraConfigured}
              onPatch={patchRow}
              onStateChanged={refreshQuietly}
              announce={announce}
            />
          ) : (
            <>
              {/* Counts, never proportions. Five absolute figures an auditor
                  could recount by hand; a share of a whole would invite the
                  reader to chase the share instead of the clients behind it
                  (ADR-0007).

                  Live only. These describe the whole live estate rather than
                  the rows under them, and on another tab that reads as a
                  contradiction — "2 Clients" above a table of one. The Awaiting
                  consent tab has figures of its own for exactly that reason:
                  they are figures only its rows have. */}
              {!loading && liveRows.length > 0 && (
                <div className="stat-grid cl-summary">
                  <div className="stat">
                    <span className="stat-figure">{liveRows.length}</span>
                    <span className="stat-label">Clients</span>
                    <span className="stat-hint">
                      {views.counts.awaiting} still need a consent link
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-figure">{failing}</span>
                    <span className="stat-label">With controls not met</span>
                    <span className="stat-hint">At their latest assessment</span>
                  </div>
                  <div className="stat">
                    <span className="stat-figure">{broken}</span>
                    <span className="stat-label">Cannot be assessed</span>
                    <span className="stat-hint">Connection needs fixing before a refresh</span>
                  </div>
                  <div className="stat">
                    <span className="stat-figure">{neverScanned}</span>
                    <span className="stat-label">Never assessed</span>
                    <span className="stat-hint">Connected, nothing run yet</span>
                  </div>
                  <div className="stat">
                    <span className="stat-figure">{stale}</span>
                    <span className="stat-label">Assessed over a month ago</span>
                    <span className="stat-hint">Describing the tenant as it was</span>
                  </div>
                </div>
              )}

              {staff && scores !== null && (
                <div className="cl-panel">
                  <ScoreDefinitionNote definition={scores.definition} />
                </div>
              )}
              {staff && scoresError !== null && (
                <p className="note cl-panel">Scores could not be loaded: {scoresError}</p>
              )}

              <div className="tbl-wrap cl-wrap">
                <table className="tbl cl-table">
                  <thead>
                    <tr>
                      {canManage && (
                        <th scope="col" className="admin-check-col" aria-label="Select" />
                      )}
                      <SortHeader column="name" label="Client" width="name" {...headerProps} />
                      <SortHeader column="risk" label="Attention" width="risk" {...headerProps} />
                      <th scope="col" className="cl-col-conn">
                        Connection
                      </th>
                      <SortHeader
                        column="scanned"
                        label="Last assessed"
                        width="scanned"
                        {...headerProps}
                      />
                      <SortHeader
                        column="failures"
                        label="Latest assessment"
                        width="latest"
                        {...headerProps}
                      />
                      {manageable && (
                        <th scope="col" className="cl-col-manage">
                          Manage
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading
                      ? [0, 1, 2, 3, 4].map((index) => (
                          <SkeletonRow key={index} selectable={canManage} manageable={manageable} />
                        ))
                      : ordered.map((row) => (
                          <TenantRow
                            key={row.tenant.id}
                            row={row}
                            capabilities={capabilities}
                            busy={busy}
                            columns={columns}
                            checked={selected.has(row.tenant.id)}
                            onToggleSelected={toggleSelected}
                            drawer={
                              pending !== null && pending.tenantId === row.tenant.id
                                ? pending.kind
                                : null
                            }
                            onOpenRow={openRow}
                            onOpen={onOpen}
                            onError={setError}
                            onRun={run}
                            staffScore={staffScoreFor(me, scores, row.tenant.id)}
                          />
                        ))}
                  </tbody>
                </table>
              </div>

              {/* Violet and unfilled, never an error box: a control we could not
                  evidence is nearly always a licence the client has not bought,
                  and error iconography would tell an MSP they have a security
                  problem when what they have is a purchasing decision.

                  Live only, for the same reason as the tiles above: it counts
                  the estate, not the tab. */}
              {unevidenced > 0 && (
                <div className="callout callout-evidence cl-legend">
                  <span className="callout-title">
                    {plural(unevidenced, 'client')} {unevidenced === 1 ? 'has' : 'have'} controls
                    that could not be evidenced
                  </span>
                  Not evidenced means the control could not be determined — usually the tenant’s
                  licence does not expose the setting. It is never counted as met, never folded into
                  the failures, and no percentage is derived from any of these counts (ADR-0007).
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

/**
 * A fresh deployment has no clients, and the honest answer to an empty table is
 * not an empty table — it is where to go next. This is the first screen a
 * trialling MSP is likely to open, so it has to survive being seen before any
 * onboarding has happened.
 */
const EmptyEstate = () => (
  <div className="empty cl-empty">
    <span className="empty-title">No clients yet</span>
    <p className="empty-body">
      This screen ranks every client tenant you manage by what needs attention first. It fills up as
      clients connect and their first assessments complete.
    </p>
    <ol className="empty-steps">
      <li>
        Add a client — their name, and the directory (tenant) id from their Entra admin centre —
        or paste a whole list into the importer beside it.
      </li>
      <li>
        Send each client’s Global Administrator their consent link, from your own mailbox, asking
        them to open it and grant read-only access. DriftWatch never emails your clients, you do.
      </li>
      <li>
        Record that you have sent it on the Awaiting consent tab, so it can tell you who is still
        waiting and for how long.
      </li>
      <li>Run an assessment once a tenant shows as connected. Its results appear here.</li>
    </ol>
  </div>
);
